import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useData } from '../data.js';
import {
  getRecord, createRecord, updateRecord, revealContactById, ApiError,
  CONTACT_STATUS,
} from '../api.js';
import type { Contact, Activity, Deal, EmailMessage } from '../api.js';
import {
  useRecords, Drawer, Text, Area, Pick, Empty, Err, ago, shortDate, money,
} from '../ui.js';
import { DocumentsPanel, EmailLogPanel, NotesPanel } from './Panels.js';
import { Composer } from './Composer.js';

// The contact card. Kyle's spec: see sales and marketing activity, the deals
// they are on, and compose / edit / manage email — all from one screen.
//
// The action bar is the point. A CRM where logging a call takes four clicks is a
// CRM where calls do not get logged, and then the timeline lies by omission.

type Row = Contact & {
  id: string; company_id: string; lifecycle: string; title: string | null;
  notes: string | null; phone: string | null; seniority: string | null;
  status: string; engagement_score: number; last_engaged_at: string | null;
  apollo_person_id: string | null; persona_id: string | null;
};

/**
 * What to do with this person next.
 *
 * Ordered by what actually blocks the work. Suppression comes first and is
 * terminal — it is set on a negative reply and it is permanent, so every other
 * suggestion below it would be wrong. A reply outranks a draft, because a
 * person who answered is worth more than a person you were about to write to.
 */
function ContactNextAction({ c, emails, deals, onCompose, onGo, onReveal }: {
  c: Row; emails: EmailMessage[]; deals: number;
  /** Look this person up in Apollo. 1 credit. See the `!c.email` branch. */
  onReveal: () => void;
  onCompose: () => void; onGo: (t: Tab) => void;
}) {
  const inbound = emails.filter((m) => m.direction === 'inbound' || m.replied_at);
  const queued = emails.filter((m) => m.status === 'scheduled');
  const drafts = emails.filter((m) => m.status === 'draft');
  const sent = emails.filter((m) => m.status === 'sent');
  const opened = sent.filter((m) => m.opened_at);

  let what: string; let why: string;
  let cta: { label: string; go: () => void } | null = null;
  let tone: 'blocked' | 'hot' | '' = '';

  if (c.suppressed) {
    what = 'Do not contact';
    why = 'Suppressed on a negative reply. This is permanent and excludes them from every sequence.';
    tone = 'blocked';
  } else if (inbound.length) {
    what = 'They replied — answer it';
    why = 'A live thread beats anything else on this page.';
    cta = { label: 'Open emails', go: () => onGo('emails') };
    tone = 'hot';
  } else if (!c.email) {
    // IT DOES THE THING IT NAMES. This said "Get an address" and sent you to
    // the details tab — the same defect Kyle reported on the account record,
    // where a CTA named an action and only navigated. Apollo is matched on the
    // person's name plus the account's domain, so a contact typed in by hand
    // needs nothing else.
    what = 'Get an address';
    why = 'No email on file, so nothing can be sent. Apollo is matched on their '
      + 'name and the company domain.';
    cta = { label: 'Look them up · 1 credit', go: onReveal };
  } else if (queued.length) {
    what = `Send the ${queued.length} queued message${queued.length > 1 ? 's' : ''}`;
    why = 'Written and waiting in the outbox.';
    cta = { label: 'Open emails', go: () => onGo('emails') };
  } else if (drafts.length) {
    what = `Finish the draft${drafts.length > 1 ? 's' : ''}`;
    why = `${drafts.length} started, not queued.`;
    cta = { label: 'Open emails', go: () => onGo('emails') };
  } else if (opened.length && !inbound.length) {
    what = 'Opened but never answered — follow up';
    why = `${opened.length} of ${sent.length} opened. Interest without a reply is a reason to write again.`;
    cta = { label: 'Write a follow-up', go: onCompose };
  } else if (sent.length) {
    what = 'Wait, or follow up';
    why = `${sent.length} sent, no opens recorded yet.`;
    cta = { label: 'Write a follow-up', go: onCompose };
  } else if (!deals) {
    what = 'Write the first message';
    why = 'You have an address and nothing has been sent.';
    cta = { label: 'Write it', go: onCompose };
  } else {
    what = 'Keep the deal moving';
    why = 'On a deal, no open thread.';
    cta = { label: 'Write a message', go: onCompose };
  }

  return (
    <div className={`nextbar ${tone}`}>
      <div className="nextbar-icon" aria-hidden="true">
        {tone === 'blocked' ? '✕' : tone === 'hot' ? '!' : '→'}
      </div>
      <div className="nextbar-text"><strong>{what}</strong><span>{why}</span></div>
      {cta && <button className="primary" onClick={cta.go}>{cta.label}</button>}
    </div>
  );
}

const TABS = ['activity', 'emails', 'notes', 'deals', 'documents', 'details'] as const;
type Tab = typeof TABS[number];

/** Engagement, derived from what actually happened. Never typed in. */
function engagement(emails: EmailMessage[], acts: Activity[]) {
  const sent = emails.filter((m) => m.status === 'sent').length;
  const opened = emails.filter((m) => m.opened_at).length;
  const replied = emails.filter((m) => m.replied_at).length;
  const inbound = emails.filter((m) => m.direction === 'inbound').length;
  const meetings = acts.filter((a) => a.kind === 'meeting').length;
  // Replies and meetings are worth far more than opens, which are noisy and
  // increasingly fabricated by mail privacy proxies.
  const score = Math.min(100, opened * 5 + replied * 25 + inbound * 25 + meetings * 30);
  return { sent, opened, replied, inbound, meetings, score };
}

export function ContactRecord() {
  const { id } = useParams<{ id: string }>();
  const { companies, live } = useData();
  const [c, setC] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What the last credit actually bought. See reveal().
  const [note, setNote] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('activity');
  const [composing, setComposing] = useState<EmailMessage | 'new' | null>(null);
  const [logging, setLogging] = useState(false);
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const activities = useRecords<Activity & { id: string; category: string }>(
    'activities', { contact_id: id });
  const emails = useRecords<EmailMessage & { id: string }>('emails', { contact_id: id });
  const dealLinks = useRecords<{ id: string; deal_id: string; role: string | null }>(
    'deal_contacts', { contact_id: id });
  const personas = useRecords<{ id: string; name: string }>('personas');
  // The rest of the committee — the composer's recipient picker needs them.
  const siblings = useRecords<{
    id: string; name: string; email: string | null; title: string | null; suppressed: boolean;
  }>('contacts', { company_id: c?.company_id });
  const [deals, setDeals] = useState<(Deal & { id: string })[]>([]);

  const reload = () => {
    if (!id) return;
    getRecord<Row>('contacts', id).then((r) => setC(r.record)).catch(() => {});
  };

  useEffect(() => {
    if (!id) return;
    let dead = false;
    getRecord<Row>('contacts', id)
      .then((r) => { if (!dead) setC(r.record); })
      .catch((e: ApiError) => { if (!dead) setError(e.message); });
    return () => { dead = true; };
  }, [id]);

  useEffect(() => {
    if (!dealLinks.rows?.length) { setDeals([]); return; }
    let dead = false;
    Promise.all(dealLinks.rows.map((l) =>
      getRecord<Deal & { id: string }>('deals', l.deal_id).catch(() => null)))
      .then((rs) => { if (!dead) setDeals(rs.filter(Boolean).map((r) => r!.record)); });
    return () => { dead = true; };
  }, [dealLinks.rows]);

  // Only a 404 means "not found". Anything else — a dead database connection,
  // a 500, an expired secret — is a different problem, and telling Kyle the
  // contact does not exist when the server simply could not be reached sends
  // him looking for the wrong bug. It cost an hour of exactly that.
  if (error && !c) {
    const missing = /not found/i.test(error);
    return (
      <Empty
        title={missing ? 'Contact not found' : 'Could not load this contact'}
        hint={missing
          ? error
          : `${error} — the record may be fine; the server could not be reached.`}
        action={<button className="primary" onClick={() => window.location.reload()}>Retry</button>}
      />
    );
  }
  if (!c) return <p className="loading">Loading…</p>;

  const company = (companies ?? []).find((x) => x.id === c.company_id);
  const persona = (personas.rows ?? []).find((p) => p.id === c.persona_id);
  const e = engagement(emails.rows ?? [], activities.rows ?? []);
  const sales = (activities.rows ?? []).filter((a) => a.category === 'sales');
  const marketing = (activities.rows ?? []).filter((a) => a.category === 'marketing');

  /**
   * Look this person up in Apollo and buy their address. One credit.
   *
   * IT REPORTS WHAT THE CREDIT BOUGHT. This used to reload in silence, so a
   * lookup that found nothing looked identical to one that was never pressed —
   * while the credit was spent either way. Apollo charges on the attempt, not
   * on the result, and a screen that hides that teaches the operator to distrust
   * the balance.
   */
  async function reveal() {
    if (!id) return;
    if (!company) {
      // The old guard bailed here in SILENCE whenever the companies list had
      // not resolved — a paid button that did nothing at all, no spinner, no
      // request, no message. If the account genuinely cannot be found, say
      // so; a no-op teaches the operator the button is broken.
      setError('Their account has not loaded yet — give it a second and try again.');
      return;
    }
    setBusy('reveal');
    setError(null);
    setNote(null);
    try {
      const r = await revealContactById(company.slug, id);
      setNote(r.email_found
        ? `Found ${r.contact.email}.`
        : 'Apollo has no address for them. The credit is spent either way — that '
          + 'is how they charge. Try LinkedIn, or ask for an intro.');
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally { setBusy(null); }
  }

  return (
    <div className="record">
      <Link className="back" to={company ? `/companies/${company.slug}` : '/contacts'}>
        ← {company ? company.name : 'Contacts'}
      </Link>

      {/* WHO THIS IS. Facts about the person, above anything we do to them —
          same split as the account record. */}
      <header className="cohead">
        <div className="cologo cologo-fallback" aria-hidden="true">
          {c.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
        </div>
        <div className="cohead-main">
          <div className="cohead-title">
            <h1>{c.name}</h1>
            {persona && <span className="flag flag-mute">{persona.name}</span>}
            {c.suppressed && <span className="flag flag-block">suppressed</span>}
          </div>
          {c.title && <p className="codesc">{c.title}{company ? ` · ${company.name}` : ''}</p>}
          <dl className="cofacts">
            <dt>Email</dt>
            <dd>
              {c.email
                ? <a href={`mailto:${c.email}`}>{c.email}</a>
                : <span className="meta">none — enrich to get one</span>}
            </dd>
            {c.phone && <><dt>Phone</dt><dd>{c.phone}</dd></>}
            {c.seniority && <><dt>Seniority</dt><dd>{c.seniority.replace('_', ' ')}</dd></>}
            {c.linkedin && (
              <>
                <dt>LinkedIn</dt>
                <dd><a href={c.linkedin} target="_blank" rel="noreferrer">profile</a></dd>
              </>
            )}
            {c.source && <><dt>Source</dt><dd>{c.source}</dd></>}
            <dt>Status</dt><dd>{c.status}</dd>
          </dl>
        </div>
        <div className="cohead-side">
          <label className="fieldlabel">Status</label>
          <select value={c.status}
            onChange={(ev) => void updateRecord('contacts', c.id, { status: ev.target.value })
              .then(reload)}>
            {CONTACT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setTab('details')}>Edit details</button>
        </div>
      </header>

      <ContactNextAction
        c={c} emails={emails.rows ?? []} deals={deals.length}
        onCompose={() => setComposing('new')} onGo={setTab}
        onReveal={() => void reveal()}
      />

      {/* What the last credit bought, said plainly. Apollo charges on the
          attempt, so "no address" is a real outcome and not a failure to hide. */}
      {note && <p className="ok-note">{note}</p>}

      {/* Everything you can do to this person, one click deep. */}
      <div className="actionbar">
        <button className="primary" onClick={() => setComposing('new')}>Email</button>
        <button onClick={() => setLogging(true)}>Log activity</button>
        <button onClick={() => setLinking(true)}>Add to deal</button>
        {/* "Enrich" is what the API does; "look them up" is what Kyle is doing.
            For a contact typed off a leadership page there is nothing to enrich
            yet — the whole point is finding them. */}
        <button disabled={!live || !!c.email || busy === 'reveal'} onClick={() => void reveal()}>
          {busy === 'reveal' ? 'Looking them up…'
            : c.email ? 'Address on file' : 'Look up in Apollo · 1 credit'}
        </button>
        {c.linkedin && (
          <a className="btnlink" href={c.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
        )}
        <button className={c.suppressed ? '' : 'danger'}
          onClick={() => void updateRecord('contacts', c.id, {
            suppressed: !c.suppressed,
            suppressed_at: c.suppressed ? null : new Date().toISOString(),
          }).then(reload)}>
          {c.suppressed ? 'Un-suppress' : 'Suppress'}
        </button>
      </div>

      <div className="stats">
        <div className="stat"><span>Engagement</span><strong>{e.score}</strong></div>
        <div className="stat"><span>Sent</span><strong>{e.sent}</strong></div>
        <div className="stat"><span>Opened</span><strong>{e.opened}</strong></div>
        <div className="stat"><span>Replied</span><strong>{e.replied + e.inbound}</strong></div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t}
            {t === 'emails' && emails.rows?.length ? <span className="tcount">{emails.rows.length}</span> : null}
            {t === 'deals' && deals.length ? <span className="tcount">{deals.length}</span> : null}
            {t === 'activity' && activities.rows?.length ? <span className="tcount">{activities.rows.length}</span> : null}
          </button>
        ))}
      </div>

      <Err>{error}</Err>

      <div className="pane">
        {tab === 'activity' && (
          <>
            <section>
              <h4>Sales</h4>
              {sales.length === 0 && <p className="meta">Nothing logged yet.</p>}
              {sales.map((a) => (
                <div className="event" key={a.id}>
                  <div className="row">
                    <span className="rname">{a.summary}</span>
                    <span className="flag flag-mute">{a.kind}</span>
                    <span className="meta">{ago(a.occurred_at)}</span>
                  </div>
                  {a.detail && <p className="meta">{a.detail}</p>}
                </div>
              ))}
            </section>
            <section>
              <h4>Marketing</h4>
              {marketing.length === 0 && (
                <p className="meta">
                  Opens, clicks, and replies land here automatically once mail is sending.
                </p>
              )}
              {marketing.map((a) => (
                <div className="event" key={a.id}>
                  <div className="row">
                    <span className="rname">{a.summary}</span>
                    <span className="meta">{ago(a.occurred_at)}</span>
                  </div>
                </div>
              ))}
            </section>
          </>
        )}

        {tab === 'emails' && (
          <EmailLogPanel contactId={c.id}
            onCompose={() => setComposing('new')}
            onEdit={(m) => setComposing(m as unknown as EmailMessage)} />
        )}

        {tab === 'deals' && (
          <>
            {deals.length === 0 && (
              <Empty title="Not on any deal"
                hint="A deal with no named buyer is a deal that slips."
                action={<button className="primary" onClick={() => setLinking(true)}>Add to a deal</button>} />
            )}
            {deals.map((d) => {
              const link = dealLinks.rows?.find((l) => l.deal_id === d.id);
              return (
                <div className="row" key={d.id}>
                  <span className="rname">{d.name}</span>
                  <span className="flag flag-mute">{link?.role?.replace('_', ' ') ?? 'no role'}</span>
                  <span className="meta">{d.stage} · {money(d.amount)}</span>
                  <span className="meta">{shortDate(d.expected_close)}</span>
                  <span className="flags">
                    <button className="mini" onClick={() => link && void dealLinks.remove(link.id)}>
                      Remove
                    </button>
                  </span>
                </div>
              );
            })}
          </>
        )}

        {tab === 'notes' && <NotesPanel companyId={c.company_id} contactId={c.id} />}

        {tab === 'documents' && <DocumentsPanel contactId={c.id} />}

        {tab === 'details' && <Details c={c} onSaved={setC} />}
      </div>

      {composing && (
        <Composer
          target={{
            companyId: c.company_id,
            contactId: c.id,
            // Everyone on the account, so the picker works the same here as on
            // the account record — Kyle asked to be able to switch recipient
            // without leaving the composer.
            recipients: (siblings.rows ?? []).map((x) => ({
              id: x.id, name: x.name, email: x.email,
              title: x.title, suppressed: x.suppressed,
            })),
            existing: composing === 'new' ? null : {
              id: composing.id, subject: composing.subject, body: composing.body,
              body_html: (composing as { body_html?: string | null }).body_html,
              status: composing.status, kind: 'email',
            },
          }}
          onClose={() => setComposing(null)}
          onSaved={() => void emails.reload()}
        />
      )}
      {logging && (
        <LogActivity contact={c} onClose={() => setLogging(false)}
          onSaved={() => void activities.reload()} />
      )}
      {linking && (
        <LinkDeal contact={c} onClose={() => setLinking(false)}
          onSaved={() => void dealLinks.reload()} />
      )}
    </div>
  );
}


function LogActivity({ contact, onClose, onSaved }: {
  contact: Row; onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState({ summary: '', kind: 'call', category: 'sales', detail: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer title={`Log activity — ${contact.name}`} onClose={onClose} footer={
      <button className="primary" disabled={saving || !f.summary.trim()} onClick={async () => {
        setSaving(true); setError(null);
        try {
          await createRecord('activities', {
            company_id: contact.company_id, contact_id: contact.id,
            summary: f.summary, kind: f.kind, category: f.category,
            detail: f.detail || null, occurred_at: new Date().toISOString(),
          });
          onSaved(); onClose();
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
        }
      }}>{saving ? 'Saving…' : 'Log it'}</button>
    }>
      <Err>{error}</Err>
      <Text label="What happened" value={f.summary} autoFocus
        placeholder="Left a voicemail — mentioned the hh2 breakdown"
        onChange={(e) => setF((p) => ({ ...p, summary: e.target.value }))} />
      <div className="pair">
        <Pick label="Type" value={f.kind} allowEmpty={false}
          options={['call', 'meeting', 'email', 'linkedin', 'note', 'research']
            .map((v) => ({ value: v, label: v }))}
          onChange={(v) => setF((p) => ({ ...p, kind: v }))} />
        <Pick label="Category" value={f.category} allowEmpty={false}
          options={[{ value: 'sales', label: 'sales' }, { value: 'marketing', label: 'marketing' }]}
          onChange={(v) => setF((p) => ({ ...p, category: v }))} />
      </div>
      <Area label="Detail" rows={5} value={f.detail}
        onChange={(v) => setF((p) => ({ ...p, detail: v }))} />
    </Drawer>
  );
}

function LinkDeal({ contact, onClose, onSaved }: {
  contact: Row; onClose: () => void; onSaved: () => void;
}) {
  const deals = useRecords<Deal & { id: string }>('deals', { company_id: contact.company_id });
  const [dealId, setDealId] = useState('');
  const [role, setRole] = useState('champion');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer title={`Add ${contact.name} to a deal`} onClose={onClose} footer={
      <button className="primary" disabled={saving || !dealId} onClick={async () => {
        setSaving(true); setError(null);
        try {
          await createRecord('deal_contacts', { deal_id: dealId, contact_id: contact.id, role });
          onSaved(); onClose();
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
        }
      }}>{saving ? 'Saving…' : 'Add to deal'}</button>
    }>
      <Err>{error}</Err>
      {deals.rows?.length === 0 && (
        <p className="warnbox">
          No deals on this account yet. Open one from the account record first.
        </p>
      )}
      <Pick label="Deal" value={dealId}
        options={(deals.rows ?? []).map((d) => ({ value: d.id, label: d.name }))}
        onChange={setDealId} />
      <Pick label="Their role" value={role} allowEmpty={false}
        options={['decision_maker', 'economic_buyer', 'champion', 'influencer', 'blocker', 'user']
          .map((v) => ({ value: v, label: v.replace('_', ' ') }))}
        onChange={setRole} />
    </Drawer>
  );
}

function Details({ c, onSaved }: { c: Row; onSaved: (next: Row) => void }) {
  const s = (k: string) => {
    const v = (c as unknown as Record<string, unknown>)[k];
    return v == null ? '' : String(v);
  };
  const [f, setF] = useState({
    title: c.title ?? '', email: c.email ?? '', phone: c.phone ?? '',
    linkedin: c.linkedin ?? '', notes: c.notes ?? '', status: c.status,
    mobile: s('mobile'), city: s('city'), state: s('state'),
    country: s('country'), timezone: s('timezone'),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  return (
    <>
      <Err>{error}</Err>
      <Text label="Title" value={f.title} onChange={(e) => set('title', e.target.value)} />
      <Text label="Email" type="email" value={f.email}
        onChange={(e) => set('email', e.target.value)} />
      {/* A switchboard number and a cell are not interchangeable at 4pm, so
          they are two fields rather than one. */}
      <div className="pair">
        <Text label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
        <Text label="Mobile" value={f.mobile} onChange={(e) => set('mobile', e.target.value)} />
      </div>
      <div className="pair">
        <Pick label="Status" value={f.status} allowEmpty={false}
          options={CONTACT_STATUS.map((o) => ({ value: o, label: o }))}
          onChange={(v) => set('status', v)} />
        <Text label="Time zone" value={f.timezone} placeholder="America/New_York"
          hint="So you know what time it is where they are."
          onChange={(e) => set('timezone', e.target.value)} />
      </div>
      <div className="pair">
        <Text label="City" value={f.city} onChange={(e) => set('city', e.target.value)} />
        <Text label="State / region" value={f.state}
          onChange={(e) => set('state', e.target.value)} />
      </div>
      <Text label="Country" value={f.country} onChange={(e) => set('country', e.target.value)} />
      <Text label="LinkedIn" value={f.linkedin} onChange={(e) => set('linkedin', e.target.value)} />
      <Area label="Notes" rows={5} value={f.notes} onChange={(v) => set('notes', v)} />
      <div className="actions">
        <button className="primary" disabled={saving} onClick={async () => {
          setSaving(true); setError(null);
          try {
            const r = await updateRecord<Row>('contacts', c.id, {
              ...f,
              email_status: f.email ? 'revealed' : c.email_status,
            });
            onSaved(r.record);
          } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
          } finally { setSaving(false); }
        }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </>
  );
}
